import { defineStore } from 'pinia';
import api from 'dashboard/routes/dashboard/zuwadAssistant/assistantApi';

export const ACTIVE_STATUSES = ['queued', 'running', 'waiting_for_approval'];
// Answers the asker should look at; the same list drives the unread badge and push on the server.
export const NOTIFY_STATUSES = ['done', 'failed', 'waiting_for_approval'];

const pageVisible = () =>
  typeof document === 'undefined' || document.visibilityState === 'visible';

const byRecent = (a, b) =>
  (b.last_message_at || b.created_at || '').localeCompare(
    a.last_message_at || a.created_at || ''
  );

// Shared by the Zuwad AI page and the chat bubble. Live updates arrive over ActionCable
// (`zuwad_assistant.message`); the server copy is the truth and is reloaded after a reconnect.
export const useZuwadAssistantStore = defineStore('zuwadAssistant', {
  state: () => ({
    accountId: null,
    threads: [],
    messages: {},
    activeThreadId: null,
    configured: true,
    loaded: false,
    sending: false,
    error: '',
    // Sharing: the viewer's id, their teams, all teams of the account and whether they see all.
    me: null,
    teams: [],
    myTeamIds: [],
    supervisor: false,
    // The chat on screen right now (page or open bubble); its answers count as seen.
    visibleThreadId: null,
    // Set by a browser notification click; the bubble component opens the chat.
    requestedThreadId: null,
    reloadTimer: null,
  }),

  getters: {
    activeThread: state =>
      state.threads.find(thread => thread.id === state.activeThreadId) || null,
    activeMessages: state => state.messages[state.activeThreadId] || [],
    unreadTotal: state =>
      state.threads.reduce((sum, thread) => sum + (thread.unread || 0), 0),
    isAnswering() {
      return Object.values(this.messages).some(list =>
        list.some(message => ACTIVE_STATUSES.includes(message.status))
      );
    },
  },

  actions: {
    async load(accountId) {
      this.accountId = accountId;
      const result = await api.threads(accountId);
      this.threads = result.threads;
      this.configured = result.configured;
      this.me = result.me ?? null;
      this.teams = result.teams || [];
      this.myTeamIds = result.my_team_ids || [];
      this.supervisor = !!result.supervisor;
      this.loaded = true;
    },

    async open(threadId) {
      this.activeThreadId = threadId;
      if (!threadId) return;
      const result = await api.thread(this.accountId, threadId);
      this.upsertThread(result.thread);
      this.messages = { ...this.messages, [threadId]: result.messages };
      if (this.visibleThreadId === threadId) this.markSeen(threadId);
    },

    // Called by the chat view when it shows a thread (or stops showing one).
    setVisibleThread(threadId) {
      this.visibleThreadId = threadId;
      if (threadId) this.markSeen(threadId);
    },

    async markSeen(threadId) {
      const thread = this.threads.find(item => item.id === threadId);
      if (!thread?.unread || !pageVisible()) return;
      this.upsertThread({ ...thread, unread: 0 });
      try {
        await api.markSeen(this.accountId, threadId);
      } catch {
        // The badge comes back on the next load; nothing else depends on it.
      }
    },

    async newThread() {
      const result = await api.createThread(this.accountId);
      this.upsertThread(result.thread);
      this.messages = { ...this.messages, [result.thread.id]: [] };
      this.activeThreadId = result.thread.id;
      return result.thread;
    },

    async send(content, files = []) {
      this.error = '';
      this.sending = true;
      try {
        const threadId = this.activeThreadId || (await this.newThread()).id;
        const result = await api.send(this.accountId, threadId, content, files);
        this.upsertThread(result.thread);
        result.messages.forEach(message => this.upsertMessage(message));
      } catch (error) {
        this.error = error?.response?.data?.error || 'failed';
        throw error;
      } finally {
        this.sending = false;
      }
    },

    async stop(message) {
      const result = await api.stop(
        this.accountId,
        message.thread_id,
        message.id
      );
      this.upsertMessage(result.message);
    },

    async approve(message, choice) {
      await api.approve(this.accountId, message.thread_id, message.id, choice);
    },

    async rename(threadId, title) {
      const result = await api.renameThread(this.accountId, threadId, title);
      this.upsertThread(result.thread);
    },

    async remove(threadId) {
      await api.deleteThread(this.accountId, threadId);
      this.threads = this.threads.filter(thread => thread.id !== threadId);
      const { [threadId]: _removed, ...rest } = this.messages;
      this.messages = rest;
      if (this.activeThreadId === threadId) this.activeThreadId = null;
    },

    // ActionCable payload: the whole message as saved so far. A late frame never replaces
    // a finished message.
    applyEvent(message) {
      if (!message?.thread_id) return;
      const thread = this.threads.find(item => item.id === message.thread_id);
      // A teammate started a chat this list does not have yet: reload the list (at most every 5 s).
      if (!thread) this.reloadSoon();
      const previous = (this.messages[message.thread_id] || []).find(
        item => item.id === message.id
      );
      if (thread && message.finished_at) {
        this.upsertThread({ ...thread, last_message_at: message.finished_at });
      }
      if (this.messages[message.thread_id]) this.upsertMessage(message);
      // Only the asker is told; a teammate's answer in a shared chat is not their unread.
      const asker = message.user?.id ?? this.me;
      const becameNotable =
        asker === this.me &&
        NOTIFY_STATUSES.includes(message.status) &&
        previous?.status !== message.status &&
        !message.seen_at;
      if (thread && becameNotable) this.answerArrived(thread, message);
    },

    // A finished answer or an approval request: seen if its chat is on screen, else counted
    // as unread and announced with a browser notification.
    answerArrived(thread, message) {
      if (this.visibleThreadId === thread.id && pageVisible()) {
        this.upsertThread({ ...thread, unread: 1 });
        this.markSeen(thread.id);
        return;
      }
      const current = this.threads.find(item => item.id === thread.id);
      this.upsertThread({ ...current, unread: (current.unread || 0) + 1 });
      this.showBrowserNotification(current, message);
    },

    showBrowserNotification(thread, message) {
      if (typeof Notification === 'undefined') return;
      if (Notification.permission !== 'granted') return;
      const notification = new Notification(thread.title || 'Zuwad AI', {
        body: message.notification_body || '',
        tag: `zuwad-ai-${thread.id}`,
        icon: '/brand-assets/logo_thumbnail.svg',
      });
      notification.onclick = () => {
        window.focus();
        this.requestedThreadId = thread.id;
        notification.close();
      };
    },

    upsertMessage(message) {
      const list = [...(this.messages[message.thread_id] || [])];
      const index = list.findIndex(item => item.id === message.id);
      if (index === -1) {
        list.push(message);
      } else {
        const current = list[index];
        const currentDone = !ACTIVE_STATUSES.includes(current.status);
        if (currentDone && ACTIVE_STATUSES.includes(message.status)) return;
        list[index] = { ...current, ...message };
      }
      this.messages = { ...this.messages, [message.thread_id]: list };
    },

    upsertThread(thread) {
      const others = this.threads.filter(item => item.id !== thread.id);
      this.threads = [thread, ...others].sort(byRecent);
    },

    reloadSoon() {
      if (this.reloadTimer || !this.accountId) return;
      this.reloadTimer = setTimeout(() => {
        this.reloadTimer = null;
        this.load(this.accountId).catch(() => {});
      }, 5000);
    },

    async refresh() {
      if (!this.accountId) return;
      await this.load(this.accountId);
      if (this.activeThreadId) await this.open(this.activeThreadId);
    },
  },
});
